/* eslint-disable react/jsx-no-bind */
import React from 'react';
import {
  Box, ButtonBase, CircularProgress, TextField,
} from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import { OnPasswordCallback } from 'react-pdf/dist/cjs/shared/types';
import { PasswordResponses } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { createFileURL, FILE_SIZE, SUPPORTED_FORMATS } from './helpers';
import analytics from '../../utils/analytics';
import PlrModal from '../../feature/preloan/v3-redesign/landing/PlrModal';
import { PlrButton, plrInputSx } from '../../feature/preloan/v3-redesign/landing/ui';
import { plr } from '../../feature/preloan/v3-redesign/landing/tokens';
import {
  alertCircleIcon, checkCircleIcon, fileTextIcon, lockIcon, uploadIcon,
} from '../../feature/preloan/v3-redesign/landing/icons';

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const pagesToWords = { 1: 'una página', 2: 'dos páginas', 3: 'tres páginas' };

type DocumentCardProps = {
  title: string,
  // Texto bajo el título antes de elegir archivo ("PDF de hasta 2 páginas").
  hint: string,
  file: File | undefined,
  onFileChange: (file: File | undefined) => void,
  onFileOk: (ok: boolean) => void,
  // Canvas de cada página: si el PDF tenía clave, se sube una copia desbloqueada.
  onUnlockedPages: (refs: React.RefObject<HTMLCanvasElement>[]) => void,
  onChooseClick?: () => void,
  uploading: boolean,
  uploaded: boolean,
  type: 'liquidacion' | 'cotizaciones',
  maxPages: 1 | 2 | 3,
  // Error que decide la lista (p. ej. liquidaciones en distintos formatos).
  formatError?: string,
};

// PDF o foto (JPG/PNG). Solo el rediseño acepta fotos; el flujo anterior sigue con PDF.
const ACCEPT = 'application/pdf,image/jpeg,image/png';

type CardStatus = 'empty' | 'reading' | 'locked' | 'error' | 'ok';

export const chipButtonSx = (primary: boolean) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  height: 38,
  px: '13px',
  borderRadius: '20px',
  fontFamily: plr.font,
  fontSize: 14,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  color: primary ? '#FFFFFF' : plr.ink,
  background: primary ? plr.ink : '#FFFFFF',
  boxShadow: primary ? 'none' : `inset 0 0 0 1.5px ${plr.ink}`,
  '&:hover': { background: primary ? plr.inkHover : plr.surface },
  '&.Mui-focusVisible': { outline: `3px solid ${plr.stepCurrent}`, outlineOffset: '2px' },
} as const);

// Tarjeta de un documento del rediseño PLR (reemplaza a DocumentUploadButton): misma lectura
// del PDF con react-pdf (conteo de páginas y PDF con clave convertido a canvas), pero el
// archivo se elige desde la tarjeta y la clave se pide en una hoja (PlrModal).
export default function DocumentCard(props: DocumentCardProps) {
  const {
    title, hint, file, onFileChange, onFileOk, onUnlockedPages, onChooseClick, uploading,
    uploaded, type, maxPages, formatError,
  } = props;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const passwordCallback = React.useRef<OnPasswordCallback | null>(null);
  const [fileURL, setFileURL] = React.useState('');
  const [fileError, setFileError] = React.useState('');
  const [nPages, setNPages] = React.useState(0);
  const [needsPassword, setNeedsPassword] = React.useState(false);
  const [unlocked, setUnlocked] = React.useState(false);
  const [passwordOpen, setPasswordOpen] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [wrongPassword, setWrongPassword] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  // React 17 no tiene useId: id estable por tarjeta.
  const [inputId] = React.useState(() => `plr-doc-${Math.random().toString(36).slice(2, 9)}`);

  const pageRefs = React.useMemo(() => Array(nPages)
    .fill(null).map(() => React.createRef<HTMLCanvasElement>()), [nPages]);

  const resetState = () => {
    setFileURL('');
    setFileError('');
    setNPages(0);
    setNeedsPassword(false);
    setUnlocked(false);
    setWrongPassword(false);
    setPassword('');
    setLoaded(false);
    passwordCallback.current = null;
  };

  const handleChoose = () => {
    if (onChooseClick) onChooseClick();
    if (inputRef.current) inputRef.current.value = '';
    inputRef.current?.click();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    resetState();
    onFileOk(false);
    onFileChange(chosen);
  };

  // Validación del archivo elegido (igual que DocumentUploadButton).
  React.useEffect(() => {
    if (!file) return;
    createFileURL(file, setFileURL);
    if (file.size > FILE_SIZE) {
      setFileError('El archivo pesa más de 5 MB.');
      return;
    }
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      setFileError('El archivo tiene que ser PDF, JPG o PNG.');
      return;
    }
    if (file.type !== 'application/pdf') onFileOk(true);
  }, [file]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNPages(numPages);
    setLoaded(true);
    if (needsPassword) {
      setUnlocked(true);
      setWrongPassword(false);
      setPasswordOpen(false);
    }
    if (numPages > maxPages) {
      setFileError(`El archivo debe tener máximo ${pagesToWords[maxPages]}.`);
      return;
    }
    onFileOk(true);
  }

  const onPassword = (callback: OnPasswordCallback, reason: number) => {
    passwordCallback.current = callback;
    setNeedsPassword(true);
    setUnlocked(false);
    if (reason === PasswordResponses.INCORRECT_PASSWORD) setWrongPassword(true);
    if (reason === PasswordResponses.NEED_PASSWORD) {
      analytics.page('PLR - DOCUMENTOS', 'CONTRASEÑA PDF');
    }
    setPassword('');
    setPasswordOpen(true);
  };

  const handleSubmitPassword = () => {
    if (!password || !passwordCallback.current) return;
    setWrongPassword(false);
    passwordCallback.current(password);
  };

  const handleClosePassword = () => {
    analytics.page('PLR - DOCUMENTOS', 'PDF BLOQUEADO');
    setPasswordOpen(false);
  };

  React.useEffect(() => {
    if (pageRefs.length > 0 && unlocked) {
      onUnlockedPages(pageRefs);
      analytics.page('PLR - DOCUMENTOS', 'PDF DESBLOQUEADO');
    }
  }, [pageRefs, unlocked]);

  let status: CardStatus = 'empty';
  if (file) {
    if (fileError || formatError) status = 'error';
    else if (needsPassword && !unlocked) status = 'locked';
    else if (loaded || file.type !== 'application/pdf') status = 'ok';
    else status = 'reading';
  }

  let detail = hint;
  if (uploaded) detail = 'Enviado';
  else if (status === 'error') detail = fileError || formatError || '';
  else if (status === 'locked') detail = 'Tiene contraseña. Desbloquéalo para que podamos leerlo.';
  else if (status === 'reading') detail = 'Leyendo el archivo…';
  else if (status === 'ok') {
    let pages = nPages ? ` · ${nPages} ${nPages === 1 ? 'página' : 'páginas'}` : '';
    if (!pages && file?.type.startsWith('image/')) pages = ' · Foto';
    detail = `${unlocked ? 'Desbloqueado' : 'Listo'}${pages}`;
  }

  const iconColor = {
    empty: plr.accent, reading: plr.accent, locked: '#B7791F', error: plr.danger, ok: '#1E7B4F',
  }[status];
  let icon = fileTextIcon({ size: 20 });
  if (uploading) icon = <CircularProgress size={18} sx={{ color: plr.accent }} />;
  else if (status === 'ok' || uploaded) icon = checkCircleIcon({ size: 20, strokeWidth: 2.2 });
  else if (status === 'locked') icon = lockIcon({ size: 20 });
  else if (status === 'error') icon = alertCircleIcon({ size: 20, strokeWidth: 2.2 });

  const busy = uploading || uploaded;
  const actionLabel = file ? 'Cambiar' : 'Elegir';

  return (
    <Box
      data-pw={`document-card-${type}`}
      data-status={uploaded ? 'uploaded' : status}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: '10px', sm: '12px' },
        p: { xs: '12px 12px 12px 12px', sm: '14px 14px 14px 16px' },
        borderRadius: '16px',
        background: '#FFFFFF',
        boxShadow: `inset 0 0 0 1px ${status === 'error' ? '#E8B4B4' : plr.border}`,
      }}
    >
      {file?.type === 'application/pdf' && fileURL && (
        <Box sx={{ display: 'none' }}>
          <Document
            onPassword={onPassword}
            file={fileURL}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={() => setFileError('No pudimos abrir el archivo. Prueba con otro o contáctanos.')}
          >
            {Array.from({ length: nPages }, (_, i) => (
              <Page canvasRef={pageRefs[i]} key={i + 1} pageNumber={i + 1} />
            ))}
          </Document>
        </Box>
      )}
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          borderRadius: '12px',
          background: status === 'error' ? '#FBEDED' : plr.surface,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box sx={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px',
      }}
      >
        <Box component="span" sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 700, color: plr.ink }}>
          {title}
        </Box>
        {file && (
          <Box
            component="a"
            href={fileURL || undefined}
            target="_blank"
            rel="noreferrer"
            sx={{
              fontSize: 13,
              color: plr.body,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {file.name}
          </Box>
        )}
        <Box
          component="span"
          role={status === 'error' ? 'alert' : undefined}
          sx={{
            fontSize: 13,
            lineHeight: 1.4,
            color: status === 'error' ? plr.danger : plr.muted,
            fontWeight: status === 'error' || status === 'locked' ? 600 : 400,
          }}
        >
          {detail}
        </Box>
      </Box>
      {status === 'locked' && !busy ? (
        <ButtonBase
          type="button"
          data-pw={`unlock-${type}-button`}
          onClick={() => setPasswordOpen(true)}
          sx={chipButtonSx(true)}
        >
          Desbloquear
        </ButtonBase>
      ) : (
        <ButtonBase
          type="button"
          data-pw={`upload-${type}-button`}
          onClick={handleChoose}
          disabled={busy}
          aria-label={`${actionLabel}: ${title}`}
          sx={{ ...chipButtonSx(!file), display: busy ? 'none' : 'flex' }}
        >
          {!file && uploadIcon({ size: 16, strokeWidth: 2.2 })}
          {actionLabel}
        </ButtonBase>
      )}
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPT}
        hidden
        disabled={busy}
        onChange={handleInputChange}
      />
      <PlrModal
        open={passwordOpen}
        onClose={handleClosePassword}
        title="Este PDF tiene contraseña"
        titleId={`${inputId}-password-title`}
        testId="pdf-password-dialog"
      >
        <Box
          component="p"
          sx={{
            m: '-6px 0 0', fontSize: { xs: 14, sm: 15 }, lineHeight: 1.45, color: plr.body,
          }}
        >
          Ingrésala para que podamos leerlo. Solo la usamos para abrir este archivo.
        </Box>
        <TextField
          type="password"
          autoFocus
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') handleSubmitPassword(); }}
          placeholder="Contraseña del PDF"
          error={wrongPassword}
          helperText={wrongPassword ? 'La contraseña no es correcta. Inténtalo de nuevo.' : ' '}
          inputProps={{ 'data-pw': 'pdf-password-input', 'aria-label': 'Contraseña del PDF' }}
          sx={plrInputSx}
        />
        <PlrButton
          testId="pdf-password-submit"
          onClick={handleSubmitPassword}
          disabled={!password}
          sx={{ width: '100%' }}
        >
          Desbloquear
        </PlrButton>
      </PlrModal>
    </Box>
  );
}

DocumentCard.defaultProps = { onChooseClick: undefined, formatError: undefined };
